import m from "mithril"
import { MithrilTsxComponent } from "mithril-tsx-component"
import { Form } from "oj-form-utils"
import { FormRows } from "../../components/forms/formRow"
import { getTranslation } from "./translation"
import { getBaseUrl, mounter } from "../../utils"
import { cleanString } from "oj-utils"
import { animateClassNames, animateCreate, animateRemove } from "oj-mithriljs-utils"
import { postJSON } from "../../fetch"
import { submitSalesforceForm } from "../../salesforce"
import { Svg } from "../../svg"

declare var process: any

type IPage = {
  question: string | (() => string)
  description?: string | (() => string)
  init?(): void
  canPrev?: false
} & (
    {
      answers: {
        text: string
        fn(): void
      }[]
    }
    |
    {
      form: Form<any>
      rows: string[][]
      submit(): Promise<string | void>
      nextText: string
    }
  )

interface IState {
  page: string
  pages: Record<string, IPage>
  data: Record<string, any>
  history: string[]
  className?: string
}

const state: IState = {
  page: "typeof_order",
  history: [],
  data: {},
  pages: {
    typeof_order: {
      question: getTranslation('questionTypeofOrder'),
      description: getTranslation('descriptionTypeofOrder'),
      nextText: getTranslation('next'),
      init() {
        const ordernr = this.form.get("ordernr")
        if (ordernr.value !== "" && !this.form.validateField(ordernr))
          Object.assign(ordernr, { value: "", errors: [] })
      },
      form: new Form({
        "ordernr": Form.field("", "", {
          required: getTranslation('fieldOrdernumberMessage'),
          patterns: [
            { pattern: /^([0-9]{8,12})$/, message: "Moet bestaan uit 8 tot en met 12 cijfers" }
          ],
        }, "text", { placeholder: getTranslation('fieldOrdernumberLabel'), })
      }),
      rows: [["ordernr"]],
      async submit() {
        if (!this.form.validate())
          return

        const data = this.form.toJSON()
        const res: {
          IsPhotoPrintExpress: boolean
          StoreCity: string
          StoreHouseNumber: string
          StoreStreet: string
        } = await fetch(`${getBaseUrl()}/customerservice/SatisfactionFormOrderInfo?ordernumber=${data.ordernr}`)
          .then(x => x.status === 200 ? x.json() : undefined)
          .catch(x => undefined)

        if (res)
          Object.assign(data, res)

        if (res?.IsPhotoPrintExpress)
          handleNext("typeof_order", "contact_shop", data, 2)
        else if (data.ordernr.indexOf("9765") === 0)
          handleNext("typeof_order", "contact_customerservice", data, 1)
        else
          handleNext("typeof_order", "received_order", data, 0)
      }
    },
    received_order: {
      question: getTranslation('questionReceivedOrder'),
      answers: [
        {
          text: getTranslation('yes'),
          fn() {
            handleNext("received_order", "define_issue", true, 0)
          }
        },
        {
          text: getTranslation('no'),
          fn() {
            handleNext("received_order", "faq", false, 1)
          }
        },
      ]
    },
    faq: {
      question: getTranslation('questionFaq'),
      description: () => {
        const lastHistory = state.history[state.history.length - 1]
        if (lastHistory === 'placed_order')
          return getTranslation('descriptionFaqPlacedOrder')
        if (lastHistory === 'received_order')
          return getTranslation('descriptionFaqReceivedOrder')
      },
      answers: [
        {
          text: getTranslation('buttonFaq'),
          fn() {
            const lastHistory = state.history[state.history.length - 1]

            if (window.culture === "fr-BE" && lastHistory === 'placed_order')
              location.href = `https://foto.hema.be/fr/service-clientele/questions-frequentes/`
            else if (window.culture === "fr-BE" && lastHistory === 'received_order')
              location.href = `https://foto.hema.be/fr/service-clientele/questions-frequentes/ma-commande/`
            else if (window.culture === "nl-BE" && lastHistory === 'placed_order')
              location.href = `https://foto.hema.be/klantenservice/veelgestelde-vragen/`
            else if (window.culture === "nl-BE" && lastHistory === 'received_order')
              location.href = `https://foto.hema.be/klantenservice/veelgestelde-vragen/mijn-bestelling/`
            else if (window.culture === "nl-NL" && lastHistory === 'placed_order')
              location.href = `https://foto.hema.nl/klantenservice/veelgestelde-vragen/`
            else if (window.culture === "nl-NL" && lastHistory === 'received_order')
              location.href = `https://foto.hema.nl/klantenservice/veelgestelde-vragen/mijn-bestelling/`
          }
        },
      ]
    },
    contact_shop: {
      question: getTranslation('questionContactShop'),
      description: getTranslation('descriptionContactShop'),
      nextText: getTranslation('showShops'),
      init() {
        const d = state.data.typeof_order
        if (!d?.StoreCity)
          return

        this.form.setVal("location", `${d.StoreStreet} ${d.StoreHouseNumber}, ${d.StoreCity}`)
      },
      form: new Form({
        "location": Form.field("", getTranslation('fieldLocationLabel'), {
          required: getTranslation('fieldLocationMessage'),
        })
      }),
      rows: [["location"]],
      async submit() {
        if (!this.form.validate())
          return

        const data = this.form.toJSON()

        if (window.culture === "fr-BE")
          location.href = `https://www.hema.com/fr-be/magasins?searchKey=${data.location}`
        else if (window.culture === "nl-BE")
          location.href = `https://www.hema.com/nl-be/winkels?searchKey=${data.location}`
        else {
          location.href = `https://winkels.hema.nl/?q=${data.location}`
        }
      }
    },
    contact_customerservice: {
      question: getTranslation('questionContactCustomerSupport'),
      description: getTranslation('descriptionContactCustomerSupport'),
      nextText: getTranslation('showShops'),
      answers: [
        {
          text: getTranslation('buttonFaq'),
          fn() {
            if (window.culture === "fr-BE")
              location.href = `https://www.hema.com/fr-be/service-clientele`
            else if (window.culture === "nl-BE")
              location.href = `https://www.hema.com/nl-be/klantenservice`
            else
              location.href = `https://www.hema.nl/klantenservice`
          }
        }
      ]
    },
    define_issue: {
      question: getTranslation('questionDefineIssue'),
      answers: [
        {
          text: getTranslation('answerDefineIssue1'),
          fn() {
            handleNext("define_issue", "define_issue_details", "Product is damaged", 0)
          }
        },
        {
          text: getTranslation('answerDefineIssue2'),
          fn() {
            handleNext("define_issue", "define_issue_details", "Not satisfied with the quality of the order", 1)
          }
        },
        {
          text: getTranslation('answerDefineIssue3'),
          fn() {
            handleNext("define_issue", "define_issue_details", "I made a mistake when making my photo product", 2)
          }
        },
      ]
    },
    define_issue_details: {
      question: () => {
        if (state.data.index === 0)
          return getTranslation('questionDefineIssueDetails1')
        if (state.data.index === 1)
          return getTranslation('questionDefineIssueDetails2')
        if (state.data.index === 2)
          return getTranslation('questionDefineIssueDetails3')
      },
      description: () => {
        if (state.data.index === 0)
          return getTranslation('descriptionDefineIssueDetails1')
        if (state.data.index === 1)
          return getTranslation('descriptionDefineIssueDetails2')
        if (state.data.index === 2)
          return getTranslation('descriptionDefineIssueDetails3')
      },
      nextText: getTranslation('send'),
      form: new Form({
        "name": Form.field<any>("", getTranslation('fieldNameLabel'), {
          required: getTranslation('fieldNameMessage')
        }, "text", {}),
        "email": Form.field<any>("", getTranslation('fieldEmailLabel'), {
          required: getTranslation('fieldEmailMessage'),
          patterns: [{ pattern: Form.patterns.email, message: getTranslation('fieldEmailValidation') }],
        }, "text", {}),
        // "subject": Form.field("", getTranslation('fieldSubjectLabel'), {
        //   required: getTranslation('fieldSubjectMessage'),
        // }),
        "description": Form.field("", getTranslation('fieldDescriptionLabel'), {
          required: getTranslation('fieldDescriptionMessage'),
        }, "textarea"),
        "file": Form.field(undefined, getTranslation('fieldFileLabel'), {
          required: getTranslation('fieldFileMessage'),
        }, "image", { accept: ["image/png", "image/jpg", "image/jpeg"] })
      }),
      rows: [["name"], ["email"], ["description"], ["file"]],
      async submit() {
        if (!this.form.validate())
          return

        const data = this.form.toJSON()

        const formObj = {
          name: data.name,
          email: data.email,
          subject: `[${window.culture.split("-")[0].toLocaleUpperCase()}] -ATG- ${getTranslation(`atgSubject${state.data.index + 1}` as any)}`,
          description: data.description,
          "00N0900000Is7kI": window.culture.split("-")[1].toLocaleUpperCase(), // "NL", "FR", "BE" 
          "00N0900000FMDBp": "ATG",
          "00N0900000FMDBq": state.data.define_issue,
          "00N0900000IsH1J": "HEMA Photo",
          "00N0900000FMDBz": state.data.typeof_order.ordernr,
          priority: "High",
          type: "Photo Complaint",
          recordType: "012090000002g8k",
          orgid: `${process.env.ATG_ID}`,
          retURL: window.culture === "fr-BE" ? "https://www.foto.hema.be/fr/" : window.culture === "nl-NL" ? "https://www.foto.hema.nl" : "https://www.foto.hema.be",
        }

        submitSalesforceForm(() => Object.entries(formObj), `${process.env.ATG_URL}`)
        handleNext("define_issue_details", "success", data)
      }
    },
    success: {
      question: getTranslation('questionSuccess'),
      description: getTranslation('descriptionSuccess'),
      canPrev: false,
      answers: [
        {
          text: getTranslation('returnToHome'),
          fn() {
            if (window.culture === "fr-BE")
              location.href = `https://foto.hema.be/fr/`
            else if (window.culture === "nl-BE")
              location.href = `https://foto.hema.be`
            else {
              location.href = `https://foto.hema.nl/`
            }
          }
        },
      ]
    },
  },
}

let _next: string
const handleNext = (current: string, next: string, value: any, index?: number) => {
  state.data[current] = value
  state.data["index"] = index
  state.history.push(current)
  state.className = "next"
  m.redraw.sync()
  state.page = undefined
  _next = next
  m.redraw()
}

const handlePrev = () => {
  const prev = state.history.pop()
  state.className = "prev"
  m.redraw.sync()
  state.page = undefined
  _next = prev
  m.redraw()
}

const handleNav = () => {
  state.page = _next
  m.redraw.sync()
}

const hasPrev = () =>
  state.pages[state.page]?.canPrev === false
    ? false
    : state.history.length > 0

interface IPageAttrs {
  page: IPage
}

class PageContainer extends MithrilTsxComponent<IPageAttrs> {
  view(v: m.Vnode<IPageAttrs>) {
    const { page } = v.attrs

    if (!page)
      return

    return <Page page={page} />
  }
}

class Page extends MithrilTsxComponent<IPageAttrs> {
  error: string
  hasPrev: boolean

  oninit(v: m.Vnode<IPageAttrs>) {
    const { page } = v.attrs
    page.init?.()
    this.hasPrev = hasPrev()
  }

  view(v: m.Vnode<IPageAttrs>) {
    const { page } = v.attrs

    return <div className={cleanString("Page", state.className, animateClassNames(v), ["faq", "contact_shop"].includes(state.page) && "finish")}>
      <span class="title h2">{typeof page.question === "function" ? m.trust(page.question()) : m.trust(page.question)}</span>
      {page.description && <p>{typeof page.description === "function" ? m.trust(page.description()) : m.trust(page.description)}</p>}
      {"answers" in page
        ? [
          <div className="answers">
            {page.answers.map(x =>
              <button className={cleanString("btn", ["faq"].includes(state.page) ? "btn-secondary" : "btn-default")} onclick={() => x.fn()}>
                <span>{x.text}</span>
              </button>
            )}
          </div>,
          this.hasPrev &&
          <button className="text-link" type="button" onclick={() => handlePrev()}>
            <i class="icon"><Svg src="/FrontendWebpack/dist/assets/newIcons/arrow-left.svg" /></i>
            <span>{getTranslation('prev')}</span>
          </button>
        ]
        : <div className={`form ${state.page === "typeof_order" ? "first-step" : ""}`}>
          <FormRows rows={page.rows} form={page.form} />
          <div className="form-submit">
            {this.error && <span class="error">{this.error}</span>}
            {this.hasPrev &&
              <button className="text-link" type="button" onclick={() => handlePrev()}>
                <i class="icon"><Svg src="/FrontendWebpack/dist/assets/newIcons/arrow-left.svg" /></i>
                <span>{getTranslation('prev')}</span>
              </button>
            }
            <button className="btn btn-secondary" disabled={state.page === "typeof_order" && !page.form.get("ordernr").value} type="submit" onclick={() => {
              delete this.error
              page.submit()
                .then(x => typeof x === "string" ? this.error = getTranslation('submitError') : undefined)
                .finally(m.redraw)
            }
            }><span>{page.nextText}</span></button>
          </div>
        </div>
      }
    </div>
  }

  oncreate(v) {
    animateCreate(v)
    window.on("keypress.sf.page", e => {
      if (e.code == "Enter")
        v.dom?.querySelector("button[type=submit]")?.click()
    })
    document.querySelector(".SatisfactionForm").scrollIntoView({ behavior: "smooth", block: "start" })
  }

  onbeforeremove(v) {
    return animateRemove(v)
  }

  onremove(v) {
    handleNav()
  }
}

interface ISatisfactionForm {

}

class SatisfactionForm extends MithrilTsxComponent<ISatisfactionForm> {
  view(v: m.Vnode<ISatisfactionForm>) {
    return <div className="SatisfactionForm">
      {<PageContainer page={state.pages[state.page]} />}
    </div>
  }
}

mounter("[data-m-satisfaction-form]", (r, d = {}) => {
  m.mount(r, {
    view: v => <SatisfactionForm {...d} />
  })
})